import type {
  CourseAssistanceMode,
  GeneratedArtifact,
  KnowledgeSource,
  TeacherNote,
} from "./types";

export const COURSE_ASSISTANCE_MODES: CourseAssistanceMode[] = [
  {
    id: "solution_search",
    label: "查找教学方案",
    description:
      "在备课笔记、校级知识库、附件与允许的网络资源中检索。",
    outputHint: "给出答案、引用来源与下一步课堂行动。",
  },
  {
    id: "cheat_sheet",
    label: "整理速查表",
    description: "将备课笔记与教材知识整合为一页教师参考。",
    outputHint: "生成含例题与自查点的一页速查表。",
  },
  {
    id: "lesson_plan",
    label: "撰写课程计划",
    description: "拟定教学目标、时间分配、活动流程与课堂检查。",
    outputHint: "返回包含时间表的实操课程计划。",
  },
  {
    id: "worksheet",
    label: "生成练习卷",
    description: "生成练习题与答题指引。",
    outputHint: "返回面向学生的题目与教师参考答案。",
  },
  {
    id: "rubric",
    label: "设计评分标准",
    description: "搭建评分维度与表现等级。",
    outputHint: "返回与课程标准对齐的评分量表。",
  },
  {
    id: "misconception",
    label: "解析常见错误",
    description: "梳理学生常见误区与再教方法。",
    outputHint: "返回诊断、解释与干预方案。",
  },
  {
    id: "parent_message",
    label: "起草家长留言",
    description: "拟一封清晰、面向家长的沟通信。",
    outputHint: "返回简洁、教师可调语气的留言文本。",
  },
];

// Mock teacher-note seed data. subjectAgentId="default" so the notes
// surface across whichever agent the teacher picks in AgentPicker —
// teacher notes are personal, not agent-specific. When JZ deploys
// nova-english / nova-chemistry / nova-math, we can either pin
// per-agent notes or leave them shared; for now the default-shared
// behavior is the safer bet for a demo.
export const TEACHER_NOTES: TeacherNote[] = [
  {
    id: "note-english-perfect-tense",
    title: "现在完成时教学要点",
    body: "G7 学生混淆 have/has + 过去分词的语序。建议先用时间线图示，再切到「至今为止做过」的语义。",
    subjectAgentId: "default",
    gradeBand: "G7",
    course: "英语",
    standard: "Yilin G7 Unit 5",
    lesson: "Unit 5 Amazing Things",
    updatedAt: "2026-05-25T09:00:00Z",
    visibility: "private",
    tags: ["语法", "时态"],
  },
  {
    id: "note-english-reading-strategies",
    title: "阅读理解三步法",
    body: "面向 G7：1) 通读抓主旨；2) 标关键句与人称代词；3) 回到题目对应原文位置定位答案。",
    subjectAgentId: "default",
    gradeBand: "G7",
    course: "英语",
    standard: "Yilin G7 Reading",
    lesson: "阅读策略训练",
    updatedAt: "2026-05-20T13:30:00Z",
    visibility: "private",
    tags: ["阅读", "策略"],
  },
  {
    id: "note-english-argument-frames",
    title: "写作论证句型框架",
    body: "学生需要观点、论据、推理、反驳的句型脚手架。准备 4 组高频句式（In my opinion / For example / This shows that / However, some may argue …）。",
    subjectAgentId: "default",
    gradeBand: "G7-G8",
    course: "英语",
    standard: "写作 (议论文)",
    lesson: "议论文写作",
    updatedAt: "2026-05-15T15:15:00Z",
    visibility: "private",
    tags: ["写作", "论证"],
  },
];

export const KNOWLEDGE_SOURCES: KnowledgeSource[] = [
  {
    id: "kb-english-curriculum",
    title: "Bosong 英语教研知识库",
    scope: "approved_knowledge",
    subjectAgentId: "default",
    detail: "南京博颂学校英语教研组已审核的教学资料与课标。",
    status: "available",
  },
  {
    id: "note-english-perfect-tense",
    title: "现在完成时教学要点",
    scope: "private_note",
    subjectAgentId: "default",
    detail: "教师私人笔记，已勾选用于本次对话。",
    status: "available",
  },
  {
    id: "web-english-policy",
    title: "外部网络资料",
    scope: "external_web",
    subjectAgentId: "default",
    detail: "外部检索结果需明确标注并引用。",
    status: "available",
  },
  {
    id: "attachment-current-chat",
    title: "本次对话附件",
    scope: "conversation_attachment",
    subjectAgentId: "default",
    detail: "上传至当前对话的文件。",
    status: "searching",
  },
  {
    id: "pending-teacher-submissions",
    title: "已提交待审教师素材",
    scope: "pending_contribution",
    subjectAgentId: "default",
    detail: "已提交至校级知识库、等待审核中。",
    status: "pending",
  },
];

export const GENERATED_ARTIFACTS: GeneratedArtifact[] = [
  {
    id: "artifact-cheat-perfect-tense",
    title: "现在完成时速查表",
    kind: "cheat_sheet",
    body: "时态结构、规则/不规则过去分词对照、since/for/already/just 触发词、5 道自测题。",
    sourceIds: ["note-english-perfect-tense", "kb-english-curriculum"],
    contribution: { status: "private" },
  },
];
